import * as THREE from 'three';
import {
  ARCADE_ARROW_COLOR_HEX,
  ARCADE_ARROW_HEAD_FACTOR,
  ARCADE_ARROW_LENGTH_FACTOR,
  ARCADE_ARROW_WIDTH_M,
  ARCADE_ARROW_Z_OFFSET_M,
  ARCADE_WRONG_COLOR_HEX,
  ARCADE_WRONG_LENGTH_FACTOR,
  ARCADE_WRONG_WIDTH_M,
  ARCADE_WRONG_Z_OFFSET_M,
  DEG_TO_RAD,
  TARGETS_RING_OUTER_RADIUS_M,
} from '../config/constants';
import { createTargetMesh } from './mesh';

/**
 * Arcade target: the shared roundel ({@link createTargetMesh}) plus a billboarded arrow
 * in the disc plane pointing in the required cut direction. The group's +Z is the front
 * normal (orient with `lookAt(user)`), so the arrow reads correctly once billboarded.
 *
 * `directionDeg` follows the frontalProjection convention: 0° = down (fendente), 90° =
 * viewer's right (tondo), clockwise. The arrow material is exposed on
 * `userData['arrowMat']` so the runtime can fade it alongside the ring on hit/miss.
 */
export function createArcadeTargetMesh(directionDeg: number): THREE.Group {
  const group = createTargetMesh();
  const arrow = buildArrow(directionDeg);
  group.add(arrow);
  group.userData['arrowMat'] = arrow.material;
  return group;
}

/** A flat arrow (shaft + head) pointing in `directionDeg` within the disc's local XY plane. */
function buildArrow(directionDeg: number): THREE.Mesh {
  const length = TARGETS_RING_OUTER_RADIUS_M * ARCADE_ARROW_LENGTH_FACTOR;
  const headLen = TARGETS_RING_OUTER_RADIUS_M * ARCADE_ARROW_HEAD_FACTOR;
  const w = ARCADE_ARROW_WIDTH_M;
  const headHalfW = w * 2.2;
  const halfL = length / 2;
  const baseY = halfL - headLen;

  // Outline of an arrow pointing +Y, centered on the origin.
  const shape = new THREE.Shape();
  shape.moveTo(-w, -halfL);
  shape.lineTo(w, -halfL);
  shape.lineTo(w, baseY);
  shape.lineTo(headHalfW, baseY);
  shape.lineTo(0, halfL);
  shape.lineTo(-headHalfW, baseY);
  shape.lineTo(-w, baseY);
  shape.closePath();

  const geom = new THREE.ShapeGeometry(shape);
  const mat = new THREE.MeshBasicMaterial({
    color: ARCADE_ARROW_COLOR_HEX,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = 'target_arrow';
  mesh.position.z = ARCADE_ARROW_Z_OFFSET_M;
  // Rotate the +Y arrow so it points to localDir = (sin θ, −cos θ): the angle of that
  // vector from +X is atan2(−cos θ, sin θ), and +Y sits at +π/2.
  const theta = directionDeg * DEG_TO_RAD;
  mesh.rotation.z = Math.atan2(-Math.cos(theta), Math.sin(theta)) - Math.PI / 2;
  return mesh;
}

/**
 * A red "X" the size of the roundel, two crossed bars in the disc's local XY plane. The
 * runtime adds it to a target hit from the wrong direction and fades it out alongside the
 * miss animation; `userData['wrongMat']` exposes the shared bar material for that fade.
 *
 * Both bars share one geometry and one material, so the disposer frees each twice — that
 * is harmless (three.js `dispose()` is idempotent) and keeps a single material to fade.
 */
export function createWrongMarker(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'target_wrong';
  group.position.z = ARCADE_WRONG_Z_OFFSET_M;

  const len = TARGETS_RING_OUTER_RADIUS_M * ARCADE_WRONG_LENGTH_FACTOR;
  const geom = new THREE.PlaneGeometry(ARCADE_WRONG_WIDTH_M, len);
  const mat = new THREE.MeshBasicMaterial({
    color: ARCADE_WRONG_COLOR_HEX,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
  });
  for (const rot of [Math.PI / 4, -Math.PI / 4]) {
    const bar = new THREE.Mesh(geom, mat);
    bar.rotation.z = rot;
    group.add(bar);
  }
  group.userData['wrongMat'] = mat;
  return group;
}
